import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, Trophy, Calendar, BookOpen } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const ResultsManagement = () => {
  const { userProfile } = useRealtime();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear().toString(),
    exam: '',
    totalStudents: '',
    qualified: '',
    topRank: '',
    description: '',
    highlights: []
  });
  const [highlightInput, setHighlightInput] = useState('');
  const [filteredExam, setFilteredExam] = useState('all');

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionResults(institutionId);
      if (result.success) {
        setResults(result.results || []);
      } else {
        toast.error('Failed to load results');
      }
    } catch (error) {
      toast.error('Error loading results');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddHighlight = () => {
    if (highlightInput.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: [...prev.highlights, highlightInput.trim()]
      }));
      setHighlightInput('');
    }
  };

  const handleRemoveHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (item) => {
    setFormData({
      year: item.year || new Date().getFullYear().toString(),
      exam: item.exam || '',
      totalStudents: item.totalStudents || '',
      qualified: item.qualified || '',
      topRank: item.topRank || '',
      description: item.description || '',
      highlights: item.highlights || []
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      year: new Date().getFullYear().toString(),
      exam: '',
      totalStudents: '',
      qualified: '',
      topRank: '',
      description: '',
      highlights: []
    });
    setHighlightInput('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.year || !formData.exam) {
      toast.error('Please enter year and exam name');
      return;
    }

    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      const result = editingId
        ? await admissionsService.saveResult({
            ...formData,
            institutionId
          }, editingId)
        : await admissionsService.saveResult({
            ...formData,
            institutionId
          });

      if (result.success) {
        toast.success(editingId ? 'Result updated successfully' : 'Result added successfully');
        handleCancel();
        loadResults();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving result: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this result?')) {
      return;
    }

    try {
      const result = await admissionsService.deleteResult(id);
      if (result.success) {
        toast.success('Result deleted successfully');
        loadResults();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting result: ' + error.message);
    }
  };

  // Group results by year
  const filteredResults = filteredExam === 'all' 
    ? results 
    : results.filter(r => r.exam === filteredExam);
  
  const resultsByYear = filteredResults.reduce((acc, result) => {
    const year = result.year || 'Unknown';
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(result);
    return acc;
  }, {});

  // Get unique exams
  const uniqueExams = [...new Set(results.map(r => r.exam).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Our Results</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage results year-wise with exam switching
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Result
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Result' : 'Add New Result'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Year *
                </label>
                <input
                  type="text"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 2024"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Exam Name *
                </label>
                <input
                  type="text"
                  value={formData.exam}
                  onChange={(e) => handleInputChange('exam', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., JEE Main, NEET, UPSC"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total Students
                </label>
                <input
                  type="text"
                  value={formData.totalStudents}
                  onChange={(e) => handleInputChange('totalStudents', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Qualified
                </label>
                <input
                  type="text"
                  value={formData.qualified}
                  onChange={(e) => handleInputChange('qualified', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 350"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Top Rank
                </label>
                <input
                  type="text"
                  value={formData.topRank}
                  onChange={(e) => handleInputChange('topRank', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., AIR 45"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Additional details about the results..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Highlights
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={highlightInput}
                  onChange={(e) => setHighlightInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddHighlight();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Add a highlight and press Enter"
                />
                <button
                  onClick={handleAddHighlight}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {formData.highlights.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.highlights.map((highlight, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                    >
                      {highlight}
                      <button
                        onClick={() => handleRemoveHighlight(index)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Display - Year-wise with Exam Switching */}
      {results.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">No results yet. Add your first result!</p>
        </div>
      ) : (
        !showForm && (
          <div className="space-y-6">
            {/* Exam Filter */}
            {uniqueExams.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Exam:</span>
                  <button
                    onClick={() => setFilteredExam('all')}
                    className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                      filteredExam === 'all'
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    All
                  </button>
                  {uniqueExams.map((exam) => (
                    <button
                      key={exam}
                      onClick={() => setFilteredExam(exam)}
                      className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                        filteredExam === exam
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {exam}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Year-wise Results */}
            {Object.keys(resultsByYear)
              .sort((a, b) => b.localeCompare(a))
              .map((year) => (
                <div key={year} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{year}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resultsByYear[year].map((result) => (
                      <div
                        key={result.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {result.exam}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(result)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(result.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          {result.totalStudents && (
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <BookOpen className="w-4 h-4 text-gray-400" />
                              <span>Total Students: <strong>{result.totalStudents}</strong></span>
                            </div>
                          )}
                          {result.qualified && (
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <Trophy className="w-4 h-4 text-green-500" />
                              <span>Qualified: <strong>{result.qualified}</strong></span>
                            </div>
                          )}
                          {result.topRank && (
                            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                              <Trophy className="w-4 h-4 text-yellow-500" />
                              <span>Top Rank: <strong>{result.topRank}</strong></span>
                            </div>
                          )}
                        </div>

                        {result.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 line-clamp-2">
                            {result.description}
                          </p>
                        )}

                        {result.highlights && result.highlights.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex flex-wrap gap-1">
                              {result.highlights.map((highlight, index) => (
                                <span
                                  key={index}
                                  className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded"
                                >
                                  {highlight}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )
      )}
    </div>
  );
};

export default ResultsManagement;

